/**
 * ฟอร์มข้อมูลบริษัท (ชื่อ · เลขผู้เสียภาษี · ที่อยู่ · โทร · ผู้ลงนาม · โลโก้)
 *
 * แยกออกจากหน้าตั้งค่าของเจ้าของ (fb#176 TN Riches 2 ต.ค. 2569) เพื่อให้ฝ่ายบุคคลที่มีสิทธิ์
 * `edit_company` กรอกเองได้จากพิมพ์เขียว — เดิมการ์ดบริษัทล็อก "เจ้าของระบบแก้ไข" ทั้งที่
 * ของพวกนี้ (เลขผู้เสียภาษี ผู้ลงนามในสลิป) เป็นสิ่งที่ HR ต้องกรอกก่อนออกสลิป/50 ทวิ/หนังสือรับรอง
 *
 * ใช้ทั้งใน /owner/settings#company (เจ้าของ) และหน้า HR (OwnerOnly.tsx เมื่อมีสิทธิ์)
 * ด่านจริงอยู่ที่ worker: /owner/company-profile ใช้ requireCapability('edit_company')
 */
import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { useCompanyProfile, useUpdateCompanyProfile, type CompanyProfile } from '../hooks';
import { resolveFileUrl, uploadFile } from '../lib/api-client';
import { useToast } from './ui/toast';
import { Loading } from './layout/Loading';
import { LoadError } from './layout/LoadError';

const COMPANY_FIELDS: Array<{ key: keyof CompanyProfile; label: string; hint?: string }> = [
  { key: 'company_name', label: 'ชื่อบริษัท (ตามหนังสือรับรอง)', hint: 'เช่น บริษัท มูดาต้า จำกัด' },
  { key: 'tax_id', label: 'เลขประจำตัวผู้เสียภาษี / นิติบุคคล (13 หลัก)', hint: 'แสดงบนสลิป + 50 ทวิ' },
  { key: 'company_phone', label: 'โทรศัพท์', hint: 'เช่น 02-123-4567' },
  { key: 'company_address', label: 'ที่อยู่บริษัท', hint: 'ที่อยู่เต็มสำหรับเอกสาร' },
  { key: 'signer_name', label: 'ชื่อผู้มีอำนาจลงนาม', hint: 'เว้นว่าง = สลิประบุ "ไม่ต้องลงนาม"; ใส่ชื่อ = พิมพ์ช่องลงนามบนสลิป' },
  { key: 'signer_title', label: 'ตำแหน่งผู้ลงนาม', hint: 'เช่น กรรมการผู้จัดการ / ผู้จัดการฝ่ายบุคคล' },
];

export function CompanyProfileForm() {
  const { data: company, isLoading, error, refetch } = useCompanyProfile();
  const updateCompany = useUpdateCompanyProfile();
  const toast = useToast();
  const [companyForm, setCompanyForm] = useState<Partial<CompanyProfile>>({});
  const [companySaved, setCompanySaved] = useState(false);

  useEffect(() => {
    if (company) setCompanyForm(company);
  }, [company]);

  async function handleSaveCompany() {
    // เลขผู้เสียภาษีใช้ออก 50 ทวิ — ผิดหลักแล้วบันทึกได้เงียบ ๆ จะไปพังตอนปลายปี ไม่ใช่ตอนนี้
    const taxDigits = (companyForm.tax_id ?? '').replace(/\D/g, '');
    if (taxDigits && taxDigits.length !== 13) {
      toast.error('เลขประจำตัวผู้เสียภาษีต้องมี 13 หลัก');
      return;
    }
    try {
      // ส่งเฉพาะช่องข้อความ — path รูปบันทึกทันทีตอนอัปโหลด/ลบ ส่งซ้ำตรงนี้อาจเอาค่าเก่าทับรูปใหม่
      // (astra fb#640: กดบันทึกก่อนข้อมูลโหลดกลับเสร็จ → ล้างรูปที่เพิ่งอัปโหลด)
      const { logo_path: _l, logo_url: _lu, signature_path: _s, signature_url: _su, ...text } = companyForm;
      await updateCompany.mutateAsync(text);
      setCompanySaved(true);
      toast.success('บันทึกข้อมูลบริษัทแล้ว');
      setTimeout(() => setCompanySaved(false), 2500);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  if (isLoading) return <Loading />;
  if (error) return <LoadError error={error} onRetry={refetch} />;

  return (
    <div className="card p-4 border-brand-200">
      <div className="font-semibold mb-1">ข้อมูลบริษัท (สำหรับเอกสารสำคัญ)</div>
      <p className="text-xs text-gray-500 mb-3">ใช้พิมพ์บนหัวสลิปเงินเดือน หนังสือรับรองหักภาษี (50 ทวิ) หนังสือรับรอง/หนังสือเตือน และเอกสารราชการ</p>

      <ImageField
        label="โลโก้บริษัท (PNG/JPG ≤ 5 MB)"
        hint="แสดงมุมซ้ายบนของสลิปทุกใบ · สูงสุด 48px ในเอกสาร"
        url={companyForm.logo_url}
        hasValue={!!companyForm.logo_path}
        name="โลโก้"
        onSave={(path) => updateCompany.mutateAsync({ logo_path: path })}
      />

      {/* fb#640 รูปลายมือชื่อกรรมการ — พิมพ์บนเส้นลงนามของ 50 ทวิ · หนังสือรับรอง/หนังสือ HR · ชุดเอกสารประกาศ */}
      <ImageField
        label="รูปลายมือชื่อผู้มีอำนาจลงนาม (PNG/JPG ≤ 5 MB)"
        hint="เซ็นบนกระดาษขาว ถ่ายรูป/สแกนแล้วครอปให้พอดีลายเซ็น (พื้นใสแบบ PNG สวยที่สุด) · พิมพ์บนเส้นลงนามของ 50 ทวิ หนังสือรับรอง หนังสือเตือน และชุดเอกสารประกาศ · ไม่ใส่ = เว้นเส้นไว้เซ็นมือ · หนังสือ HR ที่ออกแล้วคงรูปเดิม · 50 ทวิ ใช้รูปปัจจุบันทุกครั้งที่ดาวน์โหลด · ชุดเอกสารประกาศต้องกดดึงข้อมูลใหม่"
        url={companyForm.signature_url}
        hasValue={!!companyForm.signature_path}
        name="รูปลายมือชื่อ"
        wide
        onSave={(path) => updateCompany.mutateAsync({ signature_path: path })}
      />

      <div className="space-y-3">
        {COMPANY_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="label">{f.label}</label>
            {f.key === 'company_address' ? (
              <textarea rows={2} className="input resize-none" value={companyForm[f.key] ?? ''} onChange={(e) => { setCompanyForm((p) => ({ ...p, [f.key]: e.target.value })); setCompanySaved(false); }} placeholder={f.hint} />
            ) : (
              <input
                className="input"
                inputMode={f.key === 'tax_id' ? 'numeric' : f.key === 'company_phone' ? 'tel' : undefined}
                value={companyForm[f.key] ?? ''}
                onChange={(e) => { setCompanyForm((p) => ({ ...p, [f.key]: e.target.value })); setCompanySaved(false); }}
                placeholder={f.hint}
              />
            )}
            {f.hint && <div className="text-xs text-gray-500 mt-1">{f.hint}</div>}
          </div>
        ))}
      </div>
      <button onClick={handleSaveCompany} disabled={updateCompany.isPending} className="btn-primary w-full mt-3">
        <Save className="w-5 h-5" /> {updateCompany.isPending ? 'กำลังบันทึก…' : companySaved ? 'บันทึกแล้ว ✓' : 'บันทึกข้อมูลบริษัท'}
      </button>
    </div>
  );
}

/** ช่องรูป (โลโก้ / ลายมือชื่อ) — อัปโหลดแล้วบันทึก path ทันที
 *  url จาก worker เป็น path สัมพัทธ์ `/file?...` ต้องผ่าน resolveFileUrl ก่อนใส่ <img>
 *  (fb#640: ไม่ผ่าน → รูปแตก ลูกค้าเห็นเป็น "นำเข้าไม่ได้" ทั้งที่อัปโหลดสำเร็จ) */
function ImageField(props: {
  label: string;
  hint: string;
  url: string | undefined;
  hasValue: boolean;
  name: string;
  wide?: boolean;
  onSave: (path: string) => Promise<unknown>;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const src = resolveFileUrl(props.url);
  const box = props.wide ? 'h-14 w-36' : 'h-14 w-14';
  return (
    <div className="mb-4">
      <label className="label">{props.label}</label>
      <div className="mt-1 flex items-center gap-3">
        {src ? (
          <img src={src} alt={props.name} className={`${box} object-contain rounded border border-gray-200 bg-white`} />
        ) : (
          <div className={`${box} rounded border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400`}>ไม่มี</div>
        )}
        <div className="flex gap-2">
          <label className={`btn-secondary ${busy ? 'opacity-60 pointer-events-none' : 'cursor-pointer'}`}>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              disabled={busy}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = ''; // allow re-selecting same file
                if (!file) return;
                if (file.size > 5 * 1024 * 1024) {
                  toast.error(`ไฟล์ใหญ่เกิน 5 MB (${(file.size / 1024 / 1024).toFixed(1)} MB) — ย่อรูปก่อนแล้วลองใหม่`);
                  return;
                }
                setBusy(true);
                try {
                  const small = await shrinkImage(file);
                  const path = await uploadFile(small, 'logo', small.name);
                  await props.onSave(path);
                  toast.success(`อัปโหลด${props.name}สำเร็จ`);
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'อัปโหลดไม่สำเร็จ');
                } finally {
                  setBusy(false);
                }
              }}
            />
            {busy ? 'กำลังอัปโหลด…' : 'อัปโหลด'}
          </label>
          {props.hasValue && (
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await props.onSave('');
                  toast.success(`ลบ${props.name}แล้ว`);
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'ลบไม่สำเร็จ');
                } finally {
                  setBusy(false);
                }
              }}
            >
              ลบ
            </button>
          )}
        </div>
      </div>
      <div className="text-xs text-gray-500 mt-1">{props.hint}</div>
    </div>
  );
}

/** ตัวพิมพ์เอกสารรับรูปไม่เกิน 512 KB (doc-kit/resolve.ts MAX_LOGO_BYTES) — เกินจะหายจากเอกสารเงียบ ๆ
 *  ย่อในเครื่องก่อนอัปโหลด: ด้านยาวสุด 800px คง PNG (พื้นใส) · ยังเกิน 450 KB → JPEG พื้นขาว */
async function shrinkImage(file: File): Promise<File> {
  const LIMIT = 450 * 1024;
  if (file.size <= LIMIT) return file;
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, 800 / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas');
  c.width = Math.round(bmp.width * scale);
  c.height = Math.round(bmp.height * scale);
  const ctx = c.getContext('2d')!;
  ctx.drawImage(bmp, 0, 0, c.width, c.height);
  const toBlob = (type: string, q?: number) => new Promise<Blob | null>((r) => c.toBlob(r, type, q));
  let blob = await toBlob('image/png');
  let ext = 'png';
  if (!blob || blob.size > LIMIT) {
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    blob = await toBlob('image/jpeg', 0.85);
    ext = 'jpg';
  }
  if (!blob || blob.size > LIMIT) throw new Error('รูปใหญ่เกินไป ย่อไม่ลง — ครอปให้เหลือเฉพาะส่วนที่ใช้แล้วลองใหม่');
  return new File([blob], `image.${ext}`, { type: blob.type });
}
